<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import FilterLinesSvg from '@/assets/svg/filter-lines-20.svg?component';
import SwitchInput from '@/components/Shared/FormInputs/SwitchInput/SwitchInput.vue';
import RadioGroupInput from '@/components/Shared/FormInputs/RadioGroupInput/RadioGroupInput.vue';
import CheckboxGroupInput from '@/components/Shared/FormInputs/CheckboxGroupInput/CheckboxGroupInput.vue';
import TooltipArrow from '@/assets/svg/tooltip-arrow.svg?component';

import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';

import { SearchConversationOrderByFields } from '@/core/conversations/conversation-index/conversation-index.type';
import type {
  OrderByDirection,
  SearchConversationIndex,
} from '@/core/conversations/conversation-index/conversation-index.type';
import {
  CANDIDATE_ORIGIN_OPTIONS,
  type CandidateOrigin,
} from '@/core/conversations/conversation-index/candidate-origin.enum';
import MeService from '@/core/shared/me/me.service';
import { ErrorService } from '@/core/shared/errors/error.service';

/**
 * Hard-disables the filter menu. Used while the account-wide search suspends
 * browsing controls — the menu overlay teleports outside the suspended
 * wrapper, so wrapper-level pointer-events/inert alone cannot cover it
 * (ONE-6912).
 */
const props = withDefaults(defineProps<{ disabled?: boolean }>(), { disabled: false });

const conversationIndexService = new ConversationIndexService();
const meService = new MeService();

const menu = ref(false);
const isMenuHovered = ref(false);

// An already-open menu overlay lives outside any suspended wrapper — close it
// the moment the control is disabled.
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) menu.value = false;
  },
);
const sortDirection = ref(conversationIndexService.conversationIndexSearch.orderBy.direction);

const searchRef = computed(() => conversationIndexService.conversationIndexSearch);
const filterLabel = computed(() => {
  const {
    onlyUnread,
    onlyCurrentUser,
    orderBy: { field: orderByField },
  } = searchRef.value;
  if (onlyCurrentUser) {
    if (onlyUnread) {
      switch (orderByField) {
        case SearchConversationOrderByFields.LAST_MESSAGE_SENT_TS:
          return 'My most recent unreads';
        case SearchConversationOrderByFields.SCORE:
          return 'My unreads by score';
        case SearchConversationOrderByFields.APPLICATION_APPLY_TS:
          return 'My unreads by application date';
        default:
          return 'My unreads';
      }
    } else {
      switch (orderByField) {
        case SearchConversationOrderByFields.LAST_MESSAGE_SENT_TS:
          return 'My most recent conversations';
        case SearchConversationOrderByFields.SCORE:
          return 'My conversations by score';
        case SearchConversationOrderByFields.APPLICATION_APPLY_TS:
          return 'My conversations by application date';
        default:
          return 'My conversations';
      }
    }
  } else {
    switch (orderByField) {
      case SearchConversationOrderByFields.LAST_MESSAGE_SENT_TS:
        return 'Most recent conversations';
      case SearchConversationOrderByFields.SCORE:
        return 'Conversations by score';
      case SearchConversationOrderByFields.APPLICATION_APPLY_TS:
        return 'Conversations by application date';
      default:
        return 'Conversations';
    }
  }
});

const isOriginNarrowed = computed(() => {
  const { origins } = searchRef.value;
  return origins.length > 0 && origins.length < CANDIDATE_ORIGIN_OPTIONS.length;
});

const hasActiveFilters = computed(
  () => isOriginNarrowed.value || searchRef.value.onlyCurrentUser || searchRef.value.onlyUnread,
);

// The label already spells out My conversations / Unreads only, so the pill counts the rest.
const nonLabelFilterCount = computed(() => [isOriginNarrowed.value].filter(Boolean).length);

const originTooltipText = computed(() => {
  const { origins } = searchRef.value;
  const labels = CANDIDATE_ORIGIN_OPTIONS.filter((option) => origins.includes(option.value)).map(
    (option) => option.label,
  );
  return `Candidate source: ${labels.join(', ')}`;
});

const activeFilterAriaLabel = computed(
  () =>
    `${nonLabelFilterCount.value} ${nonLabelFilterCount.value === 1 ? 'filter' : 'filters'} on: ${
      originTooltipText.value
    }`,
);

const updateConversationsFilter = (newValue: string) => {
  const onlyCurrentUser = newValue === 'my';

  conversationIndexService.updateConversationIndexSearch(
    {
      onlyCurrentUser,
      onlyUnread: !onlyCurrentUser ? false : searchRef.value.onlyUnread,
    },
    { resetPagination: true, resetConversationIndexes: true },
  );
};

const updateOrderByRef = (newValue: string) => {
  conversationIndexService.updateConversationIndexSearch(
    {
      orderBy: {
        field: newValue as SearchConversationOrderByFields,
        direction: sortDirection.value,
      },
    },
    { resetPagination: true, resetConversationIndexes: true },
  );
};

const updateUnreadFilter = (newValue: boolean) => {
  conversationIndexService.updateConversationIndexSearch(
    { onlyUnread: newValue },
    { resetPagination: true, resetConversationIndexes: true },
  );
};

const updateOriginFilter = (newValue: string[]) => {
  conversationIndexService.updateConversationIndexSearch(
    { origins: newValue as CandidateOrigin[] },
    { resetPagination: true, resetConversationIndexes: true },
  );
};

const clearFilters = () => {
  conversationIndexService.updateConversationIndexSearch(
    { origins: [], onlyCurrentUser: false, onlyUnread: false },
    { resetPagination: true, resetConversationIndexes: true },
  );
};

const updateMenuHovered = (value: boolean) => {
  isMenuHovered.value = value;
};

const toggleOrderDirectionRef = () => {
  const directionMap = {
    asc: 'desc',
    desc: 'asc',
  };
  sortDirection.value = directionMap[sortDirection.value] as OrderByDirection;
  updateOrderByRef(searchRef.value.orderBy.field);
};

const currentUserAndEmployerId = computed(
  () => `${meService.userProfile?.id}-${meService.employer?.id}`,
);

watch(searchRef, async () => {
  try {
    const conversationIndexSearch: Partial<SearchConversationIndex> = {
      applicationStatus: conversationIndexService.conversationIndexSearch.applicationStatus,
      onlyCurrentUser: conversationIndexService.conversationIndexSearch.onlyCurrentUser,
      onlyUnread: conversationIndexService.conversationIndexSearch.onlyUnread,
      orderBy: conversationIndexService.conversationIndexSearch.orderBy,
      origins: conversationIndexService.conversationIndexSearch.origins,
    };
    localStorage.setItem(
      `filter-query-${currentUserAndEmployerId.value}`,
      JSON.stringify(conversationIndexSearch),
    );
  } catch (error) {
    ErrorService.captureException(error);
  }
});
</script>

<template>
  <div class="mb-2 flex min-w-0 justify-end">
    <v-menu
      v-model="menu"
      :disabled="props.disabled"
      :close-on-content-click="false"
      location="bottom right"
      transition="v-scroll-y-transition"
    >
      <template v-slot:activator="{ props }">
        <div v-bind="props" class="min-w-0">
          <div
            @mouseenter="updateMenuHovered(true)"
            @mouseleave="updateMenuHovered(false)"
            class="filter-activator flex min-w-0 cursor-pointer select-none items-center space-x-1 rounded-md border border-tint-80 bg-tint-0 px-2 py-1 text-shade-800 transition-colors hover:border-tint-100"
            :class="{ '!border-tint-100 bg-tint-20': menu || isMenuHovered }"
          >
            <div
              :title="filterLabel"
              class="filter-activator-text min-w-0 truncate text-xs font-semibold text-shade-800"
              :class="{ 'text-shade-900': menu || isMenuHovered }"
            >
              {{ filterLabel }}
            </div>

            <span
              v-if="nonLabelFilterCount > 0"
              role="img"
              tabindex="0"
              :aria-label="activeFilterAriaLabel"
              class="filter-count-pill inline-flex h-[18px] shrink-0 items-center rounded-full bg-highlight-600 px-1.5 text-2xs font-bold leading-none text-white"
              data-test="conversation-index-filter-count"
            >
              +{{ nonLabelFilterCount }}
              <v-tooltip
                content-class="bg-shade-880 text-white !text-xs !py-2 !px-3 !rounded-md"
                activator="parent"
                location="bottom left"
                offset="14"
              >
                <TooltipArrow
                  alt="Tip"
                  width="8"
                  height="16"
                  class="tooltip-arrow tooltip-arrow--end fill-shade-880"
                />
                <span class="mt-[2px] block" data-test="conversation-index-filter-count-tooltip">{{
                  originTooltipText
                }}</span>
              </v-tooltip>
            </span>

            <FilterLinesSvg
              class="shrink-0"
              :class="[menu || isMenuHovered ? 'text-shade-900' : 'text-shade-800']"
            />
          </div>
        </div>
      </template>

      <div
        class="filter-popover mt-1 flex w-60 flex-col space-y-4 rounded-lg bg-tint-0 px-4 py-3 shadow-lg"
      >
        <div
          v-if="hasActiveFilters"
          class="flex items-center justify-end"
          data-test="conversation-index-filter-header"
        >
          <button
            type="button"
            class="text-xs font-semibold text-highlight-600 hover:underline"
            data-test="conversation-index-clear-filters"
            @click="clearFilters"
          >
            Clear all filters
          </button>
        </div>
        <div>
          <label class="text-2xs font-extrabold uppercase text-shade-600">view</label>
          <SwitchInput
            label="Unreads only"
            :value="searchRef.onlyUnread"
            :disabled="!searchRef.onlyCurrentUser"
            @update:model-value="updateUnreadFilter"
          />
          <v-tooltip
            v-if="!searchRef.onlyCurrentUser"
            content-class="bg-shade-880 text-white !text-xs !py-2 !px-3 !rounded-md"
            activator="parent"
            location="bottom left"
            offset="-4"
          >
            <TooltipArrow alt="Tip" width="8" height="16" class="tooltip-arrow fill-shade-880" />
            <span class="mt-[2px] block">Not available for</span>
            <span class="font-semibold">All conversations</span>
          </v-tooltip>
        </div>
        <div class="space-y-2">
          <label class="text-2xs font-extrabold uppercase text-shade-600">conversations</label>
          <RadioGroupInput
            :items="[
              { label: 'All conversations', value: 'all' },
              { label: 'My conversations', value: 'my' },
            ]"
            :value="searchRef.onlyCurrentUser ? 'my' : 'all'"
            :on-change="updateConversationsFilter"
          />
        </div>
        <div class="space-y-2">
          <label class="text-2xs font-extrabold uppercase text-shade-600">sort</label>
          <RadioGroupInput
            :items="[
              {
                label: 'Most recent message',
                value: SearchConversationOrderByFields.LAST_MESSAGE_SENT_TS,
              },
              { label: 'Applicant Score', value: SearchConversationOrderByFields.SCORE },
              {
                label: 'Date of application',
                value: SearchConversationOrderByFields.APPLICATION_APPLY_TS,
              },
            ]"
            :value="searchRef.orderBy.field"
            :on-change="updateOrderByRef"
          />
        </div>
        <div class="space-y-2">
          <label class="text-2xs font-extrabold uppercase text-shade-600">Candidate source</label>
          <CheckboxGroupInput
            :items="CANDIDATE_ORIGIN_OPTIONS"
            :value="searchRef.origins"
            :on-change="updateOriginFilter"
            data-test="conversation-index-origin-filter"
          />
        </div>
      </div>
    </v-menu>
    <div
      @click="toggleOrderDirectionRef"
      class="ml-1 flex min-h-8 min-w-8 shrink-0 cursor-pointer items-center rounded-md transition duration-300 hover:bg-tint-40"
      :class="{ 'rotate-180': sortDirection === 'asc' }"
    >
      <img src="@/assets/svg/arrows-up.svg" class="w-full" alt="Arrows up" />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.filter-activator:hover {
  & .filter-lines-icon #icon {
    stroke: black transition-colors;
  }
  & .filter-activator-text {
    @apply text-shade-900 transition-colors;
  }
}
.filter-popover {
  box-shadow: 0 5px 12px 0 rgba(0, 0, 0, 0.1);
}
.tooltip-arrow {
  transform: rotate(90deg);
  position: absolute;
  top: -10px;
}
.tooltip-arrow--end {
  left: auto;
  right: 7px;
}
</style>

<style lang="postcss">
.filter-popover {
  & .v-label {
    @apply p-1.5 text-sm leading-5 text-shade-880 opacity-100;
  }
  & .v-input--disabled {
    & .v-label {
      @apply text-tint-400 opacity-100;
    }
  }
}
</style>
